import { DEAL_KINDS } from '../wire/specialists/deal.js';
import { STAGE_ACT_ANGLES } from '../wire/specialists/stage-act.js';
/** Terminal pseudo-stage: `nextStage` returns it when nothing is left. */
export const DEAL_DONE_STAGE = 'done';
/**
 * AUT-626 — the one step of «Осмотр». In the default catalogue since OR-07; its
 * migration appends it to the saved `deals.stages`. `order: 0`: 1…6 are taken
 * and the parser rejects a repeated order (RULINGS 1).
 */
export const INSPECTION_STEP_ENTRY = {
    key: 'inspection',
    label: 'Осмотр',
    kinds: ['inspection', 'handover'],
    order: 0,
    requiresAct: false,
};
export const DEFAULT_DEAL_STAGE_CATALOG = [
    {
        key: 'searching',
        label: 'Ищем варианты',
        kinds: ['selection', 'import'],
        order: 1,
        requiresAct: false,
    },
    {
        key: 'matched',
        label: 'Подобрана',
        kinds: ['selection', 'import'],
        order: 2,
        requiresAct: false,
        selectsCar: true,
    },
    {
        key: 'purchased',
        label: 'Куплена',
        kinds: ['import'],
        order: 3,
        requiresAct: true,
        requiredAngles: [...STAGE_ACT_ANGLES],
    },
    {
        key: 'in_transit',
        label: 'В пути',
        kinds: ['import'],
        order: 4,
        requiresAct: true,
        requiredAngles: [...STAGE_ACT_ANGLES],
    },
    { key: 'customs', label: 'На таможне', kinds: ['import'], order: 5, requiresAct: false },
    {
        key: 'ready',
        label: 'Готова к выдаче',
        kinds: ['import'],
        order: 6,
        requiresAct: true,
        requiredAngles: [...STAGE_ACT_ANGLES],
    },
    // A copy, not the shared object: a caller mutating the catalogue must not reach the export.
    { ...INSPECTION_STEP_ENTRY, kinds: [...INSPECTION_STEP_ENTRY.kinds] },
];
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function parseEntry(raw) {
    if (!isRecord(raw))
        return 'entry_not_object';
    const { key, label, kinds, order, requiresAct, requiredAngles, selectsCar, retired } = raw;
    if (typeof key !== 'string' || key.length === 0 || key === DEAL_DONE_STAGE)
        return 'bad_key';
    if (typeof label !== 'string' || label.length === 0)
        return 'bad_label';
    if (!Array.isArray(kinds) || kinds.length === 0)
        return 'bad_kinds';
    const validKinds = [];
    for (const k of kinds) {
        if (typeof k !== 'string' || !DEAL_KINDS.includes(k))
            return 'bad_kind';
        validKinds.push(k);
    }
    if (typeof order !== 'number' || !Number.isInteger(order))
        return 'bad_order';
    if (typeof requiresAct !== 'boolean')
        return 'bad_requires_act';
    const entry = { key, label, kinds: validKinds, order, requiresAct };
    if (requiredAngles !== undefined) {
        if (!Array.isArray(requiredAngles) ||
            requiredAngles.some((a) => typeof a !== 'string' || a === '')) {
            return 'bad_required_angles';
        }
        entry.requiredAngles = requiredAngles;
    }
    if (requiresAct && (entry.requiredAngles === undefined || entry.requiredAngles.length === 0)) {
        return 'requires_act_without_angles';
    }
    if (selectsCar !== undefined) {
        if (typeof selectsCar !== 'boolean')
            return 'bad_selects_car';
        entry.selectsCar = selectsCar;
    }
    if (retired !== undefined) {
        if (typeof retired !== 'boolean')
            return 'bad_retired';
        entry.retired = retired;
    }
    return entry;
}
/** Manual parser (no zod in shared). Rejects duplicate keys / orders and `requiresAct` without angles. */
export function parseDealStageCatalog(raw) {
    if (!Array.isArray(raw))
        return { ok: false, error: 'not_array' };
    const catalog = [];
    const keys = new Set();
    const orders = new Set();
    for (const item of raw) {
        const entry = parseEntry(item);
        if (typeof entry === 'string')
            return { ok: false, error: entry };
        if (keys.has(entry.key))
            return { ok: false, error: 'duplicate_key' };
        if (orders.has(entry.order))
            return { ok: false, error: 'duplicate_order' };
        keys.add(entry.key);
        orders.add(entry.order);
        catalog.push(entry);
    }
    return { ok: true, catalog };
}
function activeStages(kind, catalog) {
    return catalog
        .filter((e) => e.kinds.includes(kind) && e.retired !== true)
        .sort((a, b) => a.order - b.order);
}
/**
 * Next active stage after `current`, retired ones skipped. A key unknown to the
 * catalog for this kind (or the last stage) returns `DEAL_DONE_STAGE`, so a deal never sticks.
 */
export function nextStage(kind, current, catalog) {
    const active = activeStages(kind, catalog);
    if (current === null)
        return active[0]?.key ?? DEAL_DONE_STAGE;
    const known = catalog.find((e) => e.key === current && e.kinds.includes(kind));
    if (!known)
        return DEAL_DONE_STAGE;
    const next = active.find((e) => e.order > known.order);
    return next?.key ?? DEAL_DONE_STAGE;
}
/** Catalog label → server label → key. Never throws. */
export function stageLabel(key, catalog, serverLabel) {
    const entry = catalog.find((e) => e.key === key);
    if (entry)
        return entry.label;
    if (typeof serverLabel === 'string' && serverLabel.length > 0)
        return serverLabel;
    return key;
}
/**
 * Position «index of count» over active stages (1-based). A deal standing on a
 * retired stage shows the next active one (not «0 of N»). Unknown key or null → index 0.
 */
export function stagePosition(kind, current, catalog) {
    const active = activeStages(kind, catalog);
    const count = active.length;
    if (current === null)
        return { index: 0, count };
    const exact = active.findIndex((e) => e.key === current);
    if (exact >= 0)
        return { index: exact + 1, count };
    const known = catalog.find((e) => e.key === current && e.kinds.includes(kind));
    if (!known)
        return { index: 0, count };
    const nextIdx = active.findIndex((e) => e.order > known.order);
    return nextIdx >= 0 ? { index: nextIdx + 1, count } : { index: count, count };
}
/** «Выполнен» is offered only after the last stage of the kind (R-19); no stages → always. */
export function canMarkDone(kind, current, catalog) {
    return nextStage(kind, current, catalog) === DEAL_DONE_STAGE;
}
//# sourceMappingURL=deal-stages.js.map